import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  Autocomplete as MuiAutocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";

import {
  Autocomplete,
  DirectionsRenderer,
  DirectionsService,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";

import { Plus, Trash2 } from "lucide-react";
import { useStatus } from "../../popup/NotificationContext";
import {
  useAddRouteMutation,
  useDeleteRouteMutation,
  useGetRoutesQuery,
  useUpdateRouteMutation,
} from "../../redux/logistics/logisticsApi";
import {
  useGetAllLiteDevicesQuery,
  useGetDevicesPageQuery,
  useUpdateDeviceAdvancedSettingsMutation,
} from "../../redux/admin/adminApi";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from "../../config/googleMaps";
import { getDeviceStatus as resolveVehicleStatus } from "../../utils/vehicleStatus";
import api from "../../redux/apis/api";
import "./add-route-modal.css";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "380px",
  borderRadius: "8px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };
const ASSIGN_DEVICE_PAGE_SIZE = 25;

const getDirectionsErrorMessage = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "NOT_FOUND":
      return "Source ya destination locate nahi hua. Address dobara select karo.";
    case "ZERO_RESULTS":
      return "In locations ke liye drivable route nahi mila.";
    case "MAX_WAYPOINTS_EXCEEDED":
      return "Stops zyada hain. Kuch stops hata kar dubara try karo.";
    case "OVER_QUERY_LIMIT":
      return "Route service thodi der ke liye busy hai. 5-10 second baad retry karo.";
    case "REQUEST_DENIED":
      return "Google Maps directions request denied hui. API setup verify karo.";
    case "INVALID_REQUEST":
      return "Route request incomplete hai. Source, destination aur stops verify karo.";
    default:
      return "Route preview load nahi hua. Ek baar locations reselect karke retry karo.";
  }
};

const parseRoutesPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.routes)) return payload.routes;
  return [];
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { hour12: true }).toUpperCase();
};

const getDeviceStatus = (device) => {
  return String(resolveVehicleStatus(device) || "no_data");
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("running")) return "#10b981";
  if (value.includes("stop")) return "#dc2626";
  if (value.includes("idle")) return "#f59e0b";
  if (value.includes("expired") || value.includes("not reporting") || value.includes("offline")) return "#ef4444";
  return "#ef4444";
};

const kmFromTotalDistance = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const raw = String(value).trim().toLowerCase();
  const numeric = Number(raw.replace(/km|kms|,|\s/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeStops = (stops) => {
  if (!Array.isArray(stops)) return [];
  return stops
    .map((s) => ({
      address: s?.address || s?.location || "",
      lat: s?.lat ?? null,
      lng: s?.lng ?? null,
    }))
    .filter((s) => String(s.address || "").trim());
};

const normalizeMongoId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value?._id || value?.id || "");
  return String(value);
};

const parseLatLngValue = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
};

const normalizeRoutePathPoint = (point) => {
  if (!point) return null;
  const latValue =
    typeof point.lat === "function" ? point.lat() : point.lat ?? point.latitude ?? point?.location?.lat;
  const lngValue =
    typeof point.lng === "function" ? point.lng() : point.lng ?? point.longitude ?? point?.location?.lng;
  const lat = Number(latValue);
  const lng = Number(lngValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const extractSelectedRoutePath = (route) => {
  const overviewPath = Array.isArray(route?.overview_path) ? route.overview_path : [];
  const normalizedOverview = overviewPath.map(normalizeRoutePathPoint).filter(Boolean);
  if (normalizedOverview.length >= 2) return normalizedOverview;

  const legs = Array.isArray(route?.legs) ? route.legs : [];
  const fromSteps = legs
    .flatMap((leg) => (Array.isArray(leg?.steps) ? leg.steps : []))
    .flatMap((step) => {
      const stepPath = Array.isArray(step?.path) ? step.path : [];
      return stepPath.map(normalizeRoutePathPoint).filter(Boolean);
    });

  return fromSteps.length >= 2 ? fromSteps : [];
};

const normalizeExistingRoutePath = (points) => {
  if (!Array.isArray(points)) return [];
  return points.map(normalizeRoutePathPoint).filter(Boolean);
};

const buildCsv = (rows) => {
  const headers = ["Id", "Route Name", "Source", "Destination", "No of stops", "Route length"];
  const body = rows.map((row) =>
    [row.idLabel, row.routeName, row.source, row.destination, row.stopsCount, row.routeLength]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const EmptyState = () => (
  <Stack spacing={1} alignItems="center" sx={{ py: 10, color: "#6b7280" }}>
    <Box
      sx={{
        width: 92,
        height: 92,
        borderRadius: "50%",
        bgcolor: "#fff1f2",
        display: "grid",
        placeItems: "center",
        color: "#fb7185",
        fontSize: 34,
        userSelect: "none",
      }}
    >
      ✕
    </Box>
    <Typography sx={{ fontWeight: 700 }}>No Data Available In Table</Typography>
  </Stack>
);

function RouteForm({
  mode,
  initialRoute,
  devices,
  onCancel,
  onSaved,
}) {
  const { showStatus } = useStatus();
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [addRoute, { isLoading: adding }] = useAddRouteMutation();
  const [updateRoute, { isLoading: updating }] = useUpdateRouteMutation();
  const [updateAdvancedSettings, { isLoading: assigning }] = useUpdateDeviceAdvancedSettingsMutation();

  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [sourceText, setSourceText] = useState("");
  const [destinationText, setDestinationText] = useState("");
  const [routeName, setRouteName] = useState("");
  const [stops, setStops] = useState([]);
  const [currentStop, setCurrentStop] = useState("");
  const [currentStopResolved, setCurrentStopResolved] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [deviceDropdownSearch, setDeviceDropdownSearch] = useState("");

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [shouldRenderRoute, setShouldRenderRoute] = useState(false);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [routeRequestKey, setRouteRequestKey] = useState(0);
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  const sourceRef = useRef(null);
  const destRef = useRef(null);
  const stopRef = useRef(null);
  const lastDirectionsErrorRef = useRef("");

  const resolveTypedLocation = useCallback(
    async (value, mode = "address") => {
      const raw = String(value || "").trim();
      if (!raw) return null;

      const directLatLng = parseLatLngValue(raw);
      if (directLatLng) {
        return {
          address: raw,
          lat: directLatLng.lat,
          lng: directLatLng.lng,
        };
      }

      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve) => {
          geocoder.geocode({ address: raw }, (results = [], status) => {
            if (status !== "OK" || !results[0]?.geometry?.location) {
              resolve(null);
              return;
            }
            const place = results[0];
            resolve({
              address: place.formatted_address || raw,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            });
          });
        });
        if (result) return result;
      }

      try {
        const response = await api.get("/geocode/search", { params: { q: raw } });
        const first = Array.isArray(response?.data?.results) ? response.data.results[0] : null;
        if (!first) return null;
        return {
          address: first.address || raw,
          lat: Number(first.lat),
          lng: Number(first.lng),
        };
      } catch (_) {
        return mode === "address" ? { address: raw, lat: null, lng: null } : null;
      }
    },
    []
  );

  useEffect(() => {
    const route = initialRoute || null;
    if (!route) {
      setSourceText("");
      setDestinationText("");
      setRouteName("");
      setStops([]);
      setCurrentStop("");
      setCurrentStopResolved(null);
      setSelectedDeviceId("");
      setDeviceDropdownSearch("");
      setDirectionsResponse(null);
      setShouldRenderRoute(false);
      setSelectedRouteIndex(0);
      setRouteRequestKey(0);
      setIsRouteLoading(false);
      lastDirectionsErrorRef.current = "";
      return;
    }

    setSourceText(String(route?.source?.address || route?.sourceAddress || route?.source || ""));
    setDestinationText(String(route?.destination?.address || route?.destinationAddress || route?.destination || ""));
    setRouteName(String(route?.routeName || route?.name || ""));
    setStops(
      normalizeStops(route?.stops).map((s) => ({
        location: s.address,
        lat: s.lat,
        lng: s.lng,
        stopover: true,
      }))
    );
    setCurrentStop("");
    setCurrentStopResolved(null);
    setSelectedDeviceId(
      normalizeMongoId(route?.assignedDevice?._id || route?.assignedDevice || route?.deviceId || "")
    );
    setDeviceDropdownSearch("");
    setDirectionsResponse(null);
    setIsRouteLoading(true);
    setShouldRenderRoute(true);
    setSelectedRouteIndex(0);
    setRouteRequestKey((prev) => prev + 1);
    lastDirectionsErrorRef.current = "";
  }, [initialRoute]);

  const onSourceChanged = () => {
    const place = sourceRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setSourceText(place.formatted_address);
    setDirectionsResponse(null);
    setShouldRenderRoute(false);
    setIsRouteLoading(false);
    lastDirectionsErrorRef.current = "";
  };

  const onDestChanged = () => {
    const place = destRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setDestinationText(place.formatted_address);
    setDirectionsResponse(null);
    setShouldRenderRoute(false);
    setIsRouteLoading(false);
    lastDirectionsErrorRef.current = "";
  };

  const onStopChanged = () => {
    const place = stopRef.current?.getPlace?.();
    if (!place?.formatted_address) return;
    setCurrentStop(place.formatted_address);
    setCurrentStopResolved({
      address: place.formatted_address,
      lat:
        typeof place?.geometry?.location?.lat === "function"
          ? place.geometry.location.lat()
          : null,
      lng:
        typeof place?.geometry?.location?.lng === "function"
          ? place.geometry.location.lng()
          : null,
    });
  };

  const addStop = async (event) => {
    event.preventDefault();
    if (!currentStop.trim()) return;

    const resolvedStop =
      currentStopResolved?.address && currentStopResolved.address === currentStop.trim()
        ? currentStopResolved
        : await resolveTypedLocation(currentStop, "stop");
    const nextStop = {
      location: resolvedStop?.address || currentStop.trim(),
      lat: Number.isFinite(Number(resolvedStop?.lat)) ? Number(resolvedStop.lat) : null,
      lng: Number.isFinite(Number(resolvedStop?.lng)) ? Number(resolvedStop.lng) : null,
      stopover: true,
    };

    setStops((prev) => [...prev, nextStop]);
    setCurrentStop("");
    setCurrentStopResolved(null);
    setSelectedRouteIndex(0);
    lastDirectionsErrorRef.current = "";
    if (sourceText.trim() && destinationText.trim()) {
      setIsRouteLoading(true);
      setRouteRequestKey((prev) => prev + 1);
      setShouldRenderRoute(true);
    } else {
      setShouldRenderRoute(false);
      setIsRouteLoading(false);
    }
    const input = document.getElementById("route-stop-input");
    if (input) input.value = "";
  };

  const removeStop = (index) => {
    setStops((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedRouteIndex(0);
    lastDirectionsErrorRef.current = "";
    if (sourceText.trim() && destinationText.trim()) {
      setIsRouteLoading(true);
      setRouteRequestKey((prev) => prev + 1);
      setShouldRenderRoute(true);
    } else {
      setShouldRenderRoute(false);
      setIsRouteLoading(false);
    }
  };

  const directionsCallback = useCallback((response) => {
    const status = String(response?.status || "");
    if (response?.status === "OK") {
      setIsRouteLoading(false);
      setShouldRenderRoute(false);
      setDirectionsResponse(response);
      lastDirectionsErrorRef.current = "";
      setSelectedRouteIndex((prev) => {
        const totalRoutes = Array.isArray(response?.routes) ? response.routes.length : 0;
        if (!totalRoutes) return 0;
        return prev >= totalRoutes ? 0 : prev;
      });
      return;
    }

    if (!status) return;

    setIsRouteLoading(false);
    setShouldRenderRoute(false);
    if (lastDirectionsErrorRef.current !== status) {
      lastDirectionsErrorRef.current = status;
      showStatus("error", getDirectionsErrorMessage(status));
    }
  }, [showStatus]);

  const selectedRoute = useMemo(() => {
    const routes = Array.isArray(directionsResponse?.routes) ? directionsResponse.routes : [];
    return routes[selectedRouteIndex] || routes[0] || null;
  }, [directionsResponse, selectedRouteIndex]);

  const routeOptions = useMemo(() => {
    const routes = Array.isArray(directionsResponse?.routes) ? directionsResponse.routes : [];
    return routes.map((route, index) => {
      const legs = Array.isArray(route?.legs) ? route.legs : [];
      const meters = legs.reduce((acc, leg) => acc + (leg?.distance?.value || 0), 0);
      const seconds = legs.reduce((acc, leg) => acc + (leg?.duration?.value || 0), 0);
      const summary = String(route?.summary || "").trim();
      return {
        index,
        summary: summary || `Route ${index + 1}`,
        distanceKm: meters ? meters / 1000 : 0,
        durationMin: seconds ? seconds / 60 : 0,
      };
    });
  }, [directionsResponse]);

  const distanceKm = useMemo(() => {
    const legs = selectedRoute?.legs || [];
    const meters = legs.reduce((acc, leg) => acc + (leg?.distance?.value || 0), 0);
    return meters ? meters / 1000 : 0;
  }, [selectedRoute]);

  const durationMin = useMemo(() => {
    const legs = selectedRoute?.legs || [];
    const seconds = legs.reduce((acc, leg) => acc + (leg?.duration?.value || 0), 0);
    return seconds ? seconds / 60 : 0;
  }, [selectedRoute]);

  const directionsWaypoints = useMemo(() => {
    return (Array.isArray(stops) ? stops : [])
      .map((stop) => {
        const lat = Number(stop?.lat);
        const lng = Number(stop?.lng);
        const address = String(stop?.location || "").trim();
        const location = Number.isFinite(lat) && Number.isFinite(lng)
          ? { lat, lng }
          : address;
        if (!location) return null;
        return { location, stopover: true };
      })
      .filter(Boolean);
  }, [stops]);

  const routeRenderKey = useMemo(() => {
    const waypointKey = directionsWaypoints
      .map((stop) =>
        typeof stop.location === "string"
          ? stop.location
          : `${stop.location.lat},${stop.location.lng}`
      )
      .join("|");
    return `${routeRequestKey}:${sourceText}:${destinationText}:${waypointKey}`;
  }, [destinationText, directionsWaypoints, routeRequestKey, sourceText]);

  const submit = async () => {
    if (!routeName.trim() || !sourceText.trim() || !destinationText.trim()) {
      showStatus("warning", "Source, Destination, Route Name required hain.");
      return;
    }

    const [resolvedSource, resolvedDestination, resolvedStops] = await Promise.all([
      resolveTypedLocation(sourceText, "address"),
      resolveTypedLocation(destinationText, "address"),
      Promise.all(
        stops.map(async (stop) => {
          const resolved = await resolveTypedLocation(stop.location, "stop");
          return {
            address: resolved?.address || stop.location,
            lat: Number.isFinite(Number(resolved?.lat)) ? Number(resolved.lat) : null,
            lng: Number.isFinite(Number(resolved?.lng)) ? Number(resolved.lng) : null,
            stopOrder: 0,
          };
        })
      ),
    ]);

    if (!resolvedSource || !resolvedDestination) {
      showStatus("error", "Location search failed. Place name ya lat,lng dobara try karo.");
      return;
    }

    const selectedRoadPathFromMap = extractSelectedRoutePath(selectedRoute);
    const existingRoadPath = normalizeExistingRoutePath(
      initialRoute?.routePath || initialRoute?.path || initialRoute?.polyline
    );
    const selectedRoadPath =
      selectedRoadPathFromMap.length >= 2 ? selectedRoadPathFromMap : existingRoadPath;

    const routePayload = {
      routeName: routeName.trim(),
      source: {
        address: resolvedSource.address || sourceText.trim(),
        lat: Number.isFinite(Number(resolvedSource.lat)) ? Number(resolvedSource.lat) : null,
        lng: Number.isFinite(Number(resolvedSource.lng)) ? Number(resolvedSource.lng) : null,
      },
      destination: {
        address: resolvedDestination.address || destinationText.trim(),
        lat: Number.isFinite(Number(resolvedDestination.lat)) ? Number(resolvedDestination.lat) : null,
        lng: Number.isFinite(Number(resolvedDestination.lng)) ? Number(resolvedDestination.lng) : null,
      },
      stops: resolvedStops.map((stop, idx) => ({ ...stop, stopOrder: idx + 1 })),
      routePath: selectedRoadPath,
      selectedPathSummary: String(selectedRoute?.summary || initialRoute?.selectedPathSummary || "").trim(),
      totalDistance: distanceKm ? String(distanceKm.toFixed(2)) : String(kmFromTotalDistance(initialRoute?.totalDistance || 0) || 0),
      totalDuration: durationMin ? String(Math.round(durationMin)) : String(initialRoute?.totalDuration || ""),
    };

    let saved = null;
    try {
      if (isEdit) {
        const id = initialRoute?._id || initialRoute?.id;
        saved = await updateRoute({ id, ...routePayload }).unwrap();
      } else {
        saved = await addRoute(routePayload).unwrap();
      }
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Route save failed.");
      return;
    }

    const routeId =
      saved?.data?._id ||
      saved?._id ||
      saved?.route?._id ||
      saved?.data?.id ||
      initialRoute?._id ||
      null;

    let assignWarning = "";
    if (selectedDeviceId && routeId) {
      try {
        await updateAdvancedSettings({ deviceId: selectedDeviceId, defaultRouteId: routeId }).unwrap();
      } catch (error) {
        assignWarning = error?.data?.error || error?.data?.msg || "Route saved, but device assign failed.";
      }
    }

    showStatus(
      assignWarning ? "warning" : "success",
      assignWarning || (isEdit ? "Route updated." : "Route created.")
    );

    try {
      await onSaved?.();
    } catch (_) {
      // UI refresh/close should not turn a successful save into a failure toast.
    }
  };

  const busy = adding || updating || assigning;
  const selectedDeviceOption = useMemo(
    () => devices.find((device) => String(device?._id || device?.id || "") === String(selectedDeviceId || "")) || null,
    [devices, selectedDeviceId]
  );

  if (!isLoaded) return <Box sx={{ p: 3 }}>Loading Map...</Box>;

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "460px 1fr" }, gap: 2.5, alignItems: "stretch" }}>
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 800 }}>Source *</Typography>
          <Autocomplete onLoad={(ref) => (sourceRef.current = ref)} onPlaceChanged={onSourceChanged}>
            <input
              className="ls-rt-input"
              style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "12px 12px" }}
              placeholder="Enter a location"
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              disabled={isView}
            />
          </Autocomplete>

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Destination *</Typography>
          <Autocomplete onLoad={(ref) => (destRef.current = ref)} onPlaceChanged={onDestChanged}>
            <input
              className="ls-rt-input"
              style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "12px 12px" }}
              placeholder="Enter a location"
              value={destinationText}
              onChange={(e) => setDestinationText(e.target.value)}
              disabled={isView}
            />
          </Autocomplete>

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Add Address</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 1, alignItems: "center" }}>
            <Autocomplete onLoad={(ref) => (stopRef.current = ref)} onPlaceChanged={onStopChanged}>
              <input
                id="route-stop-input"
                style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "12px 12px" }}
                placeholder="Stop:"
                value={currentStop}
                onChange={(e) => {
                  setCurrentStop(e.target.value);
                  setCurrentStopResolved(null);
                }}
                disabled={isView}
              />
            </Autocomplete>
            <IconButton onClick={addStop} disabled={isView} sx={{ border: "1px solid #111827", borderRadius: 0.8 }}>
              <Plus size={18} />
            </IconButton>
            <IconButton onClick={() => setCurrentStop("")} disabled={isView} sx={{ color: "#ef4444" }}>
              <Trash2 size={18} />
            </IconButton>
          </Box>

          {stops.length ? (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {stops.map((s, idx) => (
                <Box key={`${idx}-${s.location}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }} noWrap title={s.location}>
                    {idx + 1}. {s.location}
                  </Typography>
                  {!isView && (
                    <IconButton size="small" onClick={() => removeStop(idx)} sx={{ color: "#ef4444" }}>
                      <Trash2 size={16} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          ) : null}

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Route Name *</Typography>
          <TextField
            fullWidth
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            sx={inputSx}
            disabled={isView}
          />

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 800 }}>Assign Device (Optional)</Typography>
          <MuiAutocomplete
            options={devices}
            value={selectedDeviceOption}
            onChange={(_, value) => setSelectedDeviceId(String(value?._id || value?.id || ""))}
            inputValue={deviceDropdownSearch}
            onInputChange={(_, value, reason) => {
              if (reason === "reset" && selectedDeviceOption) {
                setDeviceDropdownSearch(selectedDeviceOption?.name || selectedDeviceOption?.vehicleNumber || selectedDeviceOption?.imei || "");
                return;
              }
              setDeviceDropdownSearch(value);
            }}
            getOptionLabel={(option) =>
              option ? `${option?.name || option?.vehicleNumber || "Device"}${option?.imei ? ` (${option.imei})` : ""}` : ""
            }
            isOptionEqualToValue={(option, value) =>
              String(option?._id || option?.id || "") === String(value?._id || value?.id || "")
            }
            disabled={isView}
            clearOnEscape
            autoHighlight
            filterOptions={(options, state) => {
              const query = String(state.inputValue || "").trim().toLowerCase();
              if (!query) return options;
              return options.filter((device) =>
                [
                  device?.name,
                  device?.vehicleNumber,
                  device?.imei,
                  device?.model,
                  device?.msdn,
                  device?.vehicleType,
                ].some((value) => String(value || "").toLowerCase().includes(query))
              );
            }}
            noOptionsText="No device found"
            ListboxProps={{
              style: {
                maxHeight: 240,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search device by name, IMEI, model"
                sx={inputSx}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={String(option?._id || option?.id || option?.imei || option?.name)}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                    {option?.name || option?.vehicleNumber || "Device"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                    {option?.imei || "No IMEI"}{option?.model ? ` • ${option.model}` : ""}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => {
                if (!sourceText.trim() || !destinationText.trim()) {
                  showStatus("warning", "Source aur destination select karo.");
                  return;
                }
                setDirectionsResponse(null);
                setSelectedRouteIndex(0);
                setIsRouteLoading(true);
                lastDirectionsErrorRef.current = "";
                setRouteRequestKey((prev) => prev + 1);
                setShouldRenderRoute(true);
              }}
              disabled={!sourceText.trim() || !destinationText.trim()}
              sx={{ bgcolor: "#fbbf24", color: "#111827", fontWeight: 900, "&:hover": { bgcolor: "#f59e0b" } }}
            >
              {isRouteLoading ? "Loading Route..." : "Show Route"}
            </Button>
          </Stack>

          {routeOptions.length > 1 ? (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 800, color: "#111827" }}>Select Path</Typography>
              {routeOptions.map((route) => (
                <Box
                  key={route.index}
                  onClick={() => setSelectedRouteIndex(route.index)}
                  sx={{
                    border: "1px solid",
                    borderColor: route.index === selectedRouteIndex ? "#2563eb" : "#e5e7eb",
                    bgcolor: route.index === selectedRouteIndex ? "#eff6ff" : "#fff",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.25,
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                    {route.summary}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                    {route.distanceKm.toFixed(2)} km • {Math.round(route.durationMin)} mins
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>

        <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e5e7eb" }}>
          <GoogleMap mapContainerStyle={mapContainerStyle} center={defaultCenter} zoom={10}>
            {stops
              .filter((stop) => Number.isFinite(Number(stop?.lat)) && Number.isFinite(Number(stop?.lng)))
              .map((stop, index) => (
                <Marker
                  key={`stop-marker-${index}-${stop.location}`}
                  position={{ lat: Number(stop.lat), lng: Number(stop.lng) }}
                  label={{
                    text: String(index + 1),
                    color: "#ffffff",
                    fontWeight: "700",
                  }}
                />
              ))}
            {shouldRenderRoute && sourceText && destinationText ? (
              <DirectionsService
                key={`route-${routeRenderKey}`}
                options={{
                  origin: sourceText,
                  destination: destinationText,
                  waypoints: directionsWaypoints,
                  optimizeWaypoints: true,
                  provideRouteAlternatives: true,
                  travelMode: "DRIVING",
                }}
                callback={directionsCallback}
              />
            ) : null}
            {directionsResponse ? <DirectionsRenderer directions={directionsResponse} options={{ routeIndex: selectedRouteIndex }} /> : null}
          </GoogleMap>
        </Box>
      </Box>

      <Box sx={{ px: 0.5, pt: 2.5, display: "flex", gap: 2 }}>
        {!isView ? (
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy}
            sx={{
              minWidth: 260,
              bgcolor: "#f43f5e",
              "&:hover": { bgcolor: "#e11d48" },
              fontWeight: 900,
              borderRadius: 1.2,
            }}
          >
            {busy ? "SUBMIT..." : "SUBMIT"}
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={onCancel}
          disabled={busy}
          sx={{
            minWidth: 260,
            bgcolor: "#6b7280",
            "&:hover": { bgcolor: "#4b5563" },
            fontWeight: 900,
            borderRadius: 1.2,
          }}
        >
          CANCEL
        </Button>
      </Box>
    </Box>
  );
}

export default function RoutePortalModal({ open = true, onClose }) {
  const { showStatus } = useStatus();
  const routesQ = useGetRoutesQuery(undefined, { skip: !open });
  const { data: devicesPayload = [] } = useGetAllLiteDevicesQuery();
  const [deleteRoute, { isLoading: isDeleting }] = useDeleteRouteMutation();
  const [updateAdvancedSettings, { isLoading: isAssigning }] = useUpdateDeviceAdvancedSettingsMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("table"); // table | create | edit | view
  const [activeRoute, setActiveRoute] = useState(null);
  const [assignRoute, setAssignRoute] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignPage, setAssignPage] = useState(1);
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [assignOverrides, setAssignOverrides] = useState(() => new Map()); // deviceId -> boolean

  const devices = useMemo(
    () => (Array.isArray(devicesPayload) ? devicesPayload : []).filter((d) => d && (d._id || d.id)),
    [devicesPayload]
  );
  const safeDevices = useMemo(
    () =>
      devices
        .filter((device) => String(device?.status || "").toLowerCase() !== "deleted")
        .sort((left, right) => String(left?.name || left?.vehicleNumber || "").localeCompare(String(right?.name || right?.vehicleNumber || ""))),
    [devices]
  );

  const routes = useMemo(() => parseRoutesPayload(routesQ.data), [routesQ.data]);

  const normalizedRoutes = useMemo(() => {
    return routes.map((route, idx) => {
      const id = route?._id || route?.id;
      return {
        raw: route,
        id,
        idLabel: route?.id || (id ? String(id).slice(-4) : String(idx + 1)),
        routeName: route?.routeName || "-",
        source: route?.source?.address || "-",
        destination: route?.destination?.address || "-",
        stopsCount: Array.isArray(route?.stops) ? route.stops.length : 0,
        routeLength: kmFromTotalDistance(route?.totalDistance).toFixed(2),
      };
    });
  }, [routes]);

  const filteredRoutes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedRoutes;
    return normalizedRoutes.filter((row) => {
      return [row.routeName, row.source, row.destination].some((v) => String(v || "").toLowerCase().includes(query));
    });
  }, [normalizedRoutes, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredRoutes.length / rowsPerPage));
  const pagedRoutes = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRoutes.slice(start, start + rowsPerPage);
  }, [filteredRoutes, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredRoutes);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "routes.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Routes CSV downloaded.");
  };

  const handleDelete = async (row) => {
    const id = row?.id;
    if (!id) return;
    if (!window.confirm("Route delete karna hai?")) return;
    try {
      await deleteRoute(id).unwrap();
      showStatus("success", "Route deleted.");
      await routesQ.refetch?.();
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete route.");
    }
  };

  const closeForm = () => {
    setActiveRoute(null);
    setMode("table");
  };

  const busy = isDeleting || isAssigning;
  const assignDeviceQueryArgs = useMemo(() => {
    if (!assignRoute?.id) return null;
    const query = String(assignSearch || "").trim();
    return {
      page: assignPage,
      limit: ASSIGN_DEVICE_PAGE_SIZE,
      lite: "true",
      includeDeleted: "false",
      includeExpired: "true",
      search: query || undefined,
    };
  }, [assignPage, assignRoute?.id, assignSearch]);

  const {
    data: assignDevicePageResponse,
    isFetching: assignDevicesFetching,
    refetch: refetchAssignDevicePage,
  } = useGetDevicesPageQuery(assignDeviceQueryArgs, {
    skip: !open || !assignRoute?.id,
    refetchOnMountOrArgChange: true,
  });

  const fallbackAssignDeviceRows = useMemo(() => {
    const query = String(assignSearch || "").trim().toLowerCase();
    const filteredRows = !query
      ? safeDevices
      : safeDevices.filter((device) =>
          [
            device?.name,
            device?.vehicleNumber,
            device?.imei,
            device?.status,
            device?.liveData?.vehicleStatus,
            device?.msdn,
            device?.model,
            device?.vehicleType,
            device?.deviceIcon,
            device?.driver?.driverName,
            device?.driver?.contactNumber,
          ].some((value) => String(value || "").toLowerCase().includes(query))
        );
    const start = (assignPage - 1) * ASSIGN_DEVICE_PAGE_SIZE;
    return filteredRows.slice(start, start + ASSIGN_DEVICE_PAGE_SIZE);
  }, [assignPage, assignSearch, safeDevices]);

  const fallbackAssignDeviceTotal = useMemo(() => {
    const query = String(assignSearch || "").trim().toLowerCase();
    if (!query) return safeDevices.length;
    return safeDevices.filter((device) =>
      [
        device?.name,
        device?.vehicleNumber,
        device?.imei,
        device?.status,
        device?.liveData?.vehicleStatus,
        device?.msdn,
        device?.model,
        device?.vehicleType,
        device?.deviceIcon,
        device?.driver?.driverName,
        device?.driver?.contactNumber,
      ].some((value) => String(value || "").toLowerCase().includes(query))
    ).length;
  }, [assignSearch, safeDevices]);

  const assignDeviceApiRows = useMemo(
    () =>
      (Array.isArray(assignDevicePageResponse?.data)
        ? assignDevicePageResponse.data
        : Array.isArray(assignDevicePageResponse?.data?.data)
          ? assignDevicePageResponse.data.data
          : [])
        .filter((device) => String(device?.status || "").toLowerCase() !== "deleted"),
    [assignDevicePageResponse]
  );

  const shouldUseAssignDeviceFallback =
    safeDevices.length > 0 &&
    !Array.isArray(assignDevicePageResponse?.data) &&
    !Array.isArray(assignDevicePageResponse?.data?.data);

  const assignDevicePageRows = shouldUseAssignDeviceFallback ? fallbackAssignDeviceRows : assignDeviceApiRows;

  const assignDeviceTotal = shouldUseAssignDeviceFallback
    ? fallbackAssignDeviceTotal
    : Number(assignDevicePageResponse?.total ?? assignDevicePageResponse?.data?.total ?? 0);
  const assignDeviceTotalPages = Math.max(1, Math.ceil(assignDeviceTotal / ASSIGN_DEVICE_PAGE_SIZE));

  const assignDevices = useMemo(() => {
    const routeId = String(assignRoute?.id || "");
    return assignDevicePageRows
      .map((device) => {
        const id = device?._id || device?.id;
        const status = getDeviceStatus(device);
        const lastUpdate = formatTimestamp(device?.lastDataReceivedAt || device?.liveData?.timestamp);
        const assignedRouteId = normalizeMongoId(device?.advancedSettings?.defaultRouteId);
        const baseChecked = routeId && String(assignedRouteId || "") === routeId;
        const overrideChecked = assignOverrides.get(String(id));
        const checked = typeof overrideChecked === "boolean" ? overrideChecked : baseChecked;
        return {
          raw: device,
          id,
          name: device?.name || device?.vehicleNumber || device?.imei || "-",
          imei: device?.imei || "-",
          status,
          lastUpdate,
          simNo: device?.msdn || "-",
          model: device?.model || "-",
          icon: device?.vehicleType || device?.deviceIcon || "Default",
          driverName: device?.driver?.driverName || "-",
          driverContact: device?.driver?.contactNumber || "-",
          checked,
        };
      })
      .filter((row) => row.id)
      .sort((left, right) => {
        if (left.checked !== right.checked) return Number(right.checked) - Number(left.checked);
        return String(left.name || "").localeCompare(String(right.name || ""));
      });
  }, [assignDevicePageRows, assignOverrides, assignRoute?.id]);

  useEffect(() => {
    if (assignPage > assignDeviceTotalPages) {
      setAssignPage(1);
    }
  }, [assignDeviceTotalPages, assignPage]);

  const toggleAssign = async (deviceRow, nextChecked) => {
    const routeId = String(assignRoute?.id || "");
    const deviceId = deviceRow?.id;
    if (!routeId || !deviceId) return;

    setPendingAssignIds((prev) => new Set(prev).add(String(deviceId)));
    try {
      await updateAdvancedSettings({
        deviceId,
        defaultRouteId: nextChecked ? routeId : null,
      }).unwrap();
      setAssignOverrides((prev) => {
        const next = new Map(prev);
        next.set(String(deviceId), Boolean(nextChecked));
        return next;
      });
      showStatus("success", nextChecked ? "Assigned." : "Unassigned.");
    } catch (error) {
      showStatus("error", error?.data?.msg || error?.data?.error || "Assign failed.");
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(String(deviceId));
        return next;
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "80vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Routes</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {mode === "table" ? (
            <>
              <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px auto" }, gap: 1.5, alignItems: "center" }}>
                  <TextField
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    sx={inputSx}
                    InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton onClick={() => { setMode("create"); setActiveRoute(null); }} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}>
                    {[10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => routesQ.refetch?.()} sx={{ color: "#6b7280" }}>
                    <RefreshIcon />
                  </IconButton>
                  <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                    <DownloadIcon />
                  </IconButton>
                </Stack>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "54vh", borderRadius: 0 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["Id", "Route Name", "Source", "Destination", "No of stops", "Route length", "Action"].map((head) => (
                        <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRoutes.length ? (
                      pagedRoutes.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{row.idLabel}</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{row.routeName}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.source}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.destination}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.stopsCount}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.routeLength}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Edit Route">
                                <IconButton
                                  size="small"
                                  onClick={() => { setActiveRoute(row.raw); setMode("edit"); }}
                                  sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                >
                                  <EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Route">
                                <IconButton
                                  size="small"
                                  onClick={() => { setActiveRoute(row.raw); setMode("view"); }}
                                  sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                >
                                  <VisibilityOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Assign Device">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setAssignRoute(row);
                                    setAssignSearch("");
                                    setAssignPage(1);
                                    setPendingAssignIds(new Set());
                                    setAssignOverrides(new Map());
                                  }}
                                  sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                >
                                  <DirectionsCarFilledOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Route">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(row)}
                                  sx={{ bgcolor: "#ef4444", borderRadius: 1 }}
                                  disabled={busy}
                                >
                                  <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <EmptyState />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "#374151" }}>
                  Showing {filteredRoutes.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredRoutes.length)} of {filteredRoutes.length} entries
                </Typography>
                <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
              </Box>
            </>
          ) : (
            <RouteForm
              mode={mode}
              initialRoute={activeRoute}
              devices={devices}
              onCancel={closeForm}
              onSaved={async () => {
                await routesQ.refetch?.();
                closeForm();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignRoute)} onClose={() => setAssignRoute(null)} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Assign Device</Typography>
            <IconButton onClick={() => setAssignRoute(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Selected Route */}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#6b7280", mb: 1.25 }}>Selected Route</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Id", "Route Name", "Source", "Destination", "No of stops", "Route length"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, color: "#6b7280" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.idLabel}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{assignRoute?.routeName}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.source}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.destination}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.stopsCount}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignRoute?.routeLength}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Search */}
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <TextField
              placeholder="Search"
              value={assignSearch}
              onChange={(e) => {
                setAssignSearch(e.target.value);
                setAssignPage(1);
              }}
              sx={{ ...inputSx, width: "100%" }}
              InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
            />
          </Box>

          {/* Devices Table */}
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb", maxHeight: "42vh" }}>
                <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    {["Vehicle No", "Assigned Driver", "Driver Mobile", "IMEI", "Status", "Last Update", "Sim No", "Model", "Icon"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, color: "#111827" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignDevices.length ? (
                    assignDevices.map((row) => {
                      const pending = pendingAssignIds.has(String(row.id));
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={row.checked}
                              disabled={pending}
                              onChange={(e) => toggleAssign(row, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{row.name}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.driverName}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.driverContact}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.imei}</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: getStatusColor(row.status) }}>
                            {String(row.status || "").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.lastUpdate}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.simNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.model}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{row.icon}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        {assignDevicesFetching ? (
                          <Stack spacing={1.25} alignItems="center" sx={{ py: 8, color: "#6b7280" }}>
                            <CircularProgress size={28} />
                            <Typography sx={{ fontWeight: 700 }}>Loading devices...</Typography>
                          </Stack>
                        ) : (
                          <EmptyState />
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {assignDeviceTotal ? (assignPage - 1) * ASSIGN_DEVICE_PAGE_SIZE + 1 : 0} to{" "}
                {Math.min(assignPage * ASSIGN_DEVICE_PAGE_SIZE, assignDeviceTotal)} of {assignDeviceTotal} devices
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={() => refetchAssignDevicePage?.()} sx={{ color: "#6b7280" }}>
                  <RefreshIcon />
                </IconButton>
                <Pagination
                  page={assignPage}
                  count={assignDeviceTotalPages}
                  onChange={(_, value) => setAssignPage(value)}
                  shape="rounded"
                  size="small"
                />
              </Stack>
            </Box>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => setAssignRoute(null)}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 900,
                  borderRadius: 1.2,
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
